/**
 * Разбор заявки из ответа гейтвея.
 *
 * Главное правило здесь — не выдумывать. Раньше маппер подставлял
 * «2-5» вместо пустого числа врачей и «—» вместо города: с заявками
 * из приложения это не всплывало, потому что там поля всегда
 * заполнены. С лидами с лендинга всплыло бы сразу — у них этих полей
 * нет вовсе, и менеджер читал бы «2–5 врачей» как слова клиента.
 *
 * Пустое остаётся пустым, а как показать пустоту, решает экран.
 */

import type {ApplicationItem, ApplicationStatus, DoctorsRange} from './types';

type Raw = Record<string, unknown>;

const asObj = (v: unknown): Raw | null => (typeof v === 'object' && v !== null ? (v as Raw) : null);
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
/** Пустая строка — та же пустота, что и null: не показываем её как значение. */
const asNullStr = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

const STATUSES: readonly ApplicationStatus[] = ['pending', 'reviewed', 'rejected'];
const RANGES: readonly DoctorsRange[] = ['2-5', '6-10', '10+'];

export const toApplicationItem = (v: unknown): ApplicationItem | null => {
  const r = asObj(v);
  if (r === null) return null;
  return {
    id: asStr(r.id),
    companyName: asNullStr(r.companyName),
    cityName: asNullStr(r.cityName),
    doctorsCount: RANGES.includes(r.doctorsCount as DoctorsRange)
      ? (r.doctorsCount as DoctorsRange)
      : null,
    fullName: asNullStr(r.fullName),
    phone: asStr(r.phone),
    /* По умолчанию считаем телефон неподтверждённым: ошибиться в эту
       сторону безопаснее — менеджер лишний раз проверит, а не отправит
       доступы на чужой номер. */
    phoneVerified: r.phoneVerified === true,
    source: asNullStr(r.source),
    comment: asNullStr(r.comment),
    status: STATUSES.includes(r.status as ApplicationStatus)
      ? (r.status as ApplicationStatus)
      : 'pending',
    convertedClinicId: asNullStr(r.convertedClinicId),
    reviewedByName: asNullStr(r.reviewedByName),
    reviewedAt: asNullStr(r.reviewedAt),
    createdAt: asStr(r.createdAt),
  };
};

/**
 * Чем подписать заявку в списке и в шапке карточки.
 *
 * У заявки из приложения это название клиники, у лида — имя человека.
 * Нет ни того ни другого — показываем телефон: он есть всегда, и это
 * честнее пустой строки.
 */
export const applicationTitle = (a: ApplicationItem): string =>
  a.companyName ?? a.fullName ?? a.phone;
